import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import FAQ from '../components/common/FAQ';
import { usePageMetadata } from '../hooks/usePageMetadata';

const Services = () => {
    // Set page metadata
    usePageMetadata('services');

    return (
        <div className="min-h-screen bg-white">
            <Navbar />

            <div className="container mx-auto px-6 py-12">
                <div className="max-w-6xl mx-auto text-center">
                    {/* Simple Coming Soon Badge */}
                    <div className="mb-8">
                        <div className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-full border border-gray-200">
                            <div className="w-2 h-2 bg-gray-600 rounded-full mr-3"></div>
                            <span className="font-medium text-sm">Coming Soon</span>
                        </div>
                    </div>

                    {/* Main Heading */}
                    <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
                        Our Services
                    </h1>

                    {/* Subtitle */}
                    <p className="text-lg text-gray-600 mb-16 leading-relaxed">
                        Comprehensive solutions designed for the diaspora community
                    </p>

                    {/* Service Cards Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                        {/* Investment Services */}
                        <div className="group relative">
                            <div className="bg-white rounded-xl p-8 shadow border border-gray-200 hover:shadow-lg transition-all duration-300">
                                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Investment Platform</h3>
                                <p className="text-gray-600 mb-4">Secure and transparent investment opportunities in real estate and businesses</p>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div className="bg-gray-600 h-2 rounded-full w-4/5"></div>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">80% Complete</p>
                            </div>
                        </div>

                        {/* Community Services */}
                        <div className="group relative">
                            <div className="bg-white rounded-xl p-8 shadow border border-gray-200 hover:shadow-lg transition-all duration-300">
                                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Community Hub</h3>
                                <p className="text-gray-600 mb-4">Connect with fellow diaspora members and build meaningful relationships</p>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div className="bg-gray-600 h-2 rounded-full w-3/5"></div>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">60% Complete</p>
                            </div>
                        </div>

                        {/* Financial Services */}
                        <div className="group relative">
                            <div className="bg-white rounded-xl p-8 shadow border border-gray-200 hover:shadow-lg transition-all duration-300">
                                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Financial Tools</h3>
                                <p className="text-gray-600 mb-4">Advanced financial planning and money transfer services</p>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div className="bg-gray-600 h-2 rounded-full w-2/5"></div>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">40% Complete</p>
                            </div>
                        </div>

                        {/* Education Services */}
                        <div className="group relative">
                            <div className="bg-white rounded-xl p-8 shadow border border-gray-200 hover:shadow-lg transition-all duration-300">
                                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Learning Center</h3>
                                <p className="text-gray-600 mb-4">Educational resources and skill development programs</p>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div className="bg-gray-600 h-2 rounded-full w-1/5"></div>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">20% Complete</p>
                            </div>
                        </div>

                        {/* Legal Services */}
                        <div className="group relative">
                            <div className="bg-white rounded-xl p-8 shadow border border-gray-200 hover:shadow-lg transition-all duration-300">
                                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Legal Support</h3>
                                <p className="text-gray-600 mb-4">Legal consultation and documentation services</p>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div className="bg-gray-600 h-2 rounded-full w-1/10"></div>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">10% Complete</p>
                            </div>
                        </div>

                        {/* Travel Services */}
                        <div className="group relative">
                            <div className="bg-white rounded-xl p-8 shadow border border-gray-200 hover:shadow-lg transition-all duration-300">
                                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Travel Services</h3>
                                <p className="text-gray-600 mb-4">Travel planning and visa assistance services</p>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div className="bg-gray-600 h-2 rounded-full w-1/20"></div>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">5% Complete</p>
                            </div>
                        </div>
                    </div>

                    {/* Overall Progress */}
                    <div className="mb-12">
                        <h3 className="text-2xl font-semibold text-gray-900 mb-4">Overall Development Progress</h3>
                        <div className="bg-gray-50 rounded-full p-3 border border-gray-200">
                            <div className="bg-gray-600 h-4 rounded-full w-2/3"></div>
                        </div>
                        <p className="text-sm text-gray-500 mt-2">Overall Progress: 67%</p>
                    </div>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/"
                            className="px-8 py-4 bg-gray-800 text-white rounded-lg font-semibold hover:bg-gray-900 transition-all duration-200"
                        >
                            Back to Home
                        </Link>
                        <button className="px-8 py-4 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200">
                            Join Waitlist
                        </button>
                    </div>
                </div>
            </div>

            {/* FAQ Section */}
            <FAQ
                title="Services & Solutions Questions"
                subtitle="Learn more about our comprehensive services for the diaspora community"
                faqs={[
                    {
                        id: 1,
                        question: "What services does Diasporawise offer?",
                        answer: "Diasporawise offers a comprehensive suite of services including investment opportunities, portfolio management, financial planning, real estate investments, technology startup investments, and educational resources. We're continuously expanding our services to better serve the diaspora community."
                    },
                    {
                        id: 2,
                        question: "Are your services available globally?",
                        answer: "Yes, our services are designed to serve the global diaspora community. We operate in multiple countries and comply with local regulations to provide investment opportunities and financial services to investors worldwide, regardless of their location."
                    },
                    {
                        id: 3,
                        question: "Do you offer personalized financial planning?",
                        answer: "We provide personalized financial planning services for our premium investors. Our certified financial planners work with you to create customized investment strategies based on your financial goals, risk tolerance, and investment timeline."
                    },
                    {
                        id: 4,
                        question: "What makes your services different from traditional investment platforms?",
                        answer: "Our services are specifically designed for the diaspora community, understanding the unique challenges and opportunities they face. We offer culturally sensitive financial advice, cross-border investment solutions, and specialized services that traditional platforms don't provide."
                    },
                    {
                        id: 5,
                        question: "How do I access your premium services?",
                        answer: "Premium services are available to investors who meet certain investment thresholds or subscribe to our premium membership. You can upgrade your account through your dashboard or contact our support team to learn more about premium service eligibility."
                    },
                    {
                        id: 6,
                        question: "Do you provide educational resources and training?",
                        answer: "Yes, we offer comprehensive educational resources including webinars, investment guides, market analysis, and one-on-one consultations. Our goal is to empower our community with the knowledge needed to make informed investment decisions."
                    }
                ]}
            />

            <Footer />
        </div>
    );
};

export default Services; 